import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, downloadBlob } from "@/lib/api";
import { MODULES } from "@/config/modules";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Plus, RefreshCw, Search, Download, FileSpreadsheet, LayoutGrid, List as ListIcon,
  MoreHorizontal, AlertTriangle, ShieldAlert, ClipboardList, ArchiveRestore, Archive,
  Pill, CalendarClock, Building2, Users2, Info, FileText, Pencil, ChevronLeft, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PersonAvatar from "@/components/PersonAvatar";
import RecordDialog from "@/components/RecordDialog";
import BulkImportDialog from "@/components/BulkImportDialog";
import { asArray, ALLERGY_SEVERITY_STYLES, getAllergySeverity } from "@/lib/clientFields";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import MobileFilterDrawer from "@/components/mobile/MobileFilterDrawer";

const PAGE_SIZE = 24;

const REASON_LABELS = {
  care_plan_overdue: { label: "Care plan review overdue", icon: CalendarClock },
  missing_info: { label: "Missing mandatory information", icon: Info },
  consent_missing: { label: "Consent missing", icon: FileText },
  consent_expired: { label: "Consent expired", icon: FileText },
  high_priority_alert: { label: "Unresolved high-priority alert", icon: ShieldAlert },
  medication_issue: { label: "Medication issue", icon: Pill },
};

function humanDate(iso, withTime = false) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const opts = withTime
      ? { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }
      : { day: "2-digit", month: "short", year: "numeric" };
    return d.toLocaleString("en-GB", opts);
  } catch { return String(iso); }
}

function CarePlanBadge({ status }) {
  const map = {
    overdue:  "bg-red-50 text-red-700 border-red-200",
    due_soon: "bg-amber-50 text-amber-800 border-amber-200",
    current:  "bg-emerald-50 text-emerald-700 border-emerald-200",
    no_date:  "bg-slate-50 text-slate-600 border-slate-200",
    no_plan:  "bg-slate-50 text-slate-500 border-slate-200",
  };
  const label = {
    overdue: "Overdue", due_soon: "Due soon", current: "Current",
    no_date: "No review date", no_plan: "No plan",
  }[status] || status;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full border ${map[status] || map.no_plan}`}>
      <ClipboardList className="w-3 h-3" aria-hidden="true" /> {label}
    </span>
  );
}

function StatusBadge({ status, archived }) {
  if (archived) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-slate-200 text-slate-700 border border-slate-300" data-testid="status-badge">
        <Archive className="w-3 h-3" aria-hidden="true" /> Archived
      </span>
    );
  }
  const isActive = (status || "active") === "active";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full border ${
        isActive
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-slate-100 text-slate-600 border-slate-200"
      }`}
      data-testid="status-badge"
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-500" : "bg-slate-400"}`} aria-hidden="true" />
      {isActive ? "Active" : (status || "Inactive").replace(/_/g, " ")}
    </span>
  );
}

function AttentionChip({ reasons }) {
  if (!reasons || reasons.length === 0) return null;
  const title = reasons.map((r) => REASON_LABELS[r]?.label || r).join(" · ");
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-amber-50 text-amber-800 border border-amber-300"
      title={title}
      aria-label={`Needs attention: ${title}`}
      data-testid="attention-chip"
    >
      <AlertTriangle className="w-3 h-3" aria-hidden="true" /> Needs attention
    </span>
  );
}

function AllergyPill({ item }) {
  const sev = getAllergySeverity(item);
  const icon = sev === "severe" ? "⚠" : sev === "moderate" ? "!" : "•";
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded-full border font-medium ${ALLERGY_SEVERITY_STYLES[sev]}`}
      title={`${item} — ${sev} risk`}
    >
      <span aria-hidden="true">{icon}</span>{item}
    </span>
  );
}

function KpiCard({ label, value, icon: Icon, tone = "default", testid }) {
  const tones = {
    default: "bg-white border-slate-200 text-slate-900",
    amber:   "bg-amber-50/60 border-amber-200 text-amber-900",
    rose:    "bg-rose-50/60 border-rose-200 text-rose-900",
    sky:     "bg-sky-50/60 border-sky-200 text-sky-900",
  };
  return (
    <div className={`rounded-lg border p-4 flex items-center justify-between ${tones[tone]}`} data-testid={testid}>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className="text-2xl font-heading font-semibold mt-1 tabular-nums">{value}</div>
      </div>
      <Icon className="w-8 h-8 opacity-30" aria-hidden="true" />
    </div>
  );
}

function ServiceUserCard({ row, onOpen, onEdit, onCarePlan, onMedication, onDocuments, onArchive, onRestore, canArchive }) {
  const displayName = row.preferred_name || row.full_name;
  const shortAllergies = (row.allergies || []).slice(0, 3);
  const extraAllergies = Math.max(0, (row.allergies || []).length - shortAllergies.length);
  const primaryNeeds = (row.primary_needs || []).slice(0, 3);
  const extraNeeds = Math.max(0, (row.primary_needs || []).length - primaryNeeds.length);

  return (
    <article
      className={`group relative rounded-xl border ${
        row.archived ? "border-slate-200 bg-slate-50/60" : "border-slate-200 bg-white"
      } shadow-sm hover:shadow-md transition-shadow flex flex-col`}
      data-testid={`client-card-${row.id}`}
    >
      {/* Attention accent bar (colour NOT sole signal — badge + icon present too) */}
      {row.needs_attention && !row.archived && (
        <div className="h-1 bg-amber-400 rounded-t-xl" aria-hidden="true" />
      )}

      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-start gap-3">
          <button
            onClick={() => onOpen(row)}
            className="shrink-0 focus:outline-none focus:ring-2 focus:ring-sky-400 rounded-full"
            aria-label={`Open profile for ${displayName}`}
            data-testid={`card-avatar-${row.id}`}
          >
            <PersonAvatar person={row} name={displayName} size="lg" />
          </button>

          <div className="min-w-0 flex-1">
            <button
              onClick={() => onOpen(row)}
              className="text-left focus:outline-none"
              data-testid={`card-name-${row.id}`}
            >
              <div className="font-heading font-semibold text-slate-900 truncate">
                {displayName}
              </div>
              {row.preferred_name && row.preferred_name !== row.full_name && (
                <div className="text-[11px] text-slate-500 truncate">Legal: {row.full_name}</div>
              )}
            </button>
            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
              <StatusBadge status={row.status} archived={row.archived} />
              <AttentionChip reasons={row.attention_reasons} />
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                aria-label={`Actions for ${displayName}`}
                data-testid={`card-menu-${row.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white w-52" data-testid={`card-menu-content-${row.id}`}>
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-500">Service User</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => onOpen(row)} data-testid={`menu-view-${row.id}`}>View Profile</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEdit(row)} data-testid={`menu-edit-${row.id}`}>Edit Details</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onCarePlan(row)} data-testid={`menu-careplan-${row.id}`}>Care Plan</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onMedication(row)} data-testid={`menu-medication-${row.id}`}>Medication</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDocuments(row)} data-testid={`menu-documents-${row.id}`}>Documents</DropdownMenuItem>
              {canArchive && !row.archived && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onArchive(row)}
                    className="text-slate-700"
                    data-testid={`menu-archive-${row.id}`}
                  ><Archive className="w-3.5 h-3.5 mr-2" /> Archive</DropdownMenuItem>
                </>
              )}
              {canArchive && row.archived && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onRestore(row)}
                    className="text-emerald-700"
                    data-testid={`menu-restore-${row.id}`}
                  ><ArchiveRestore className="w-3.5 h-3.5 mr-2" /> Restore</DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px] text-slate-700">
          <div>
            <dt className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">DOB</dt>
            <dd className="tabular-nums">
              {row.dob ? `${humanDate(row.dob)}${row.age != null ? ` · ${row.age}y` : ""}` : <span className="text-slate-400">—</span>}
            </dd>
          </div>
          <div>
            <dt className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Site / House</dt>
            <dd className="truncate flex items-center gap-1">
              <Building2 className="w-3 h-3 text-slate-400" aria-hidden="true" />
              {row.site_name || <span className="text-slate-400">Not assigned</span>}
            </dd>
          </div>
          <div>
            <dt className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Meds (active)</dt>
            <dd className="tabular-nums flex items-center gap-1">
              <Pill className="w-3 h-3 text-slate-400" aria-hidden="true" />
              {row.active_med_count}
            </dd>
          </div>
          <div>
            <dt className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Last note</dt>
            <dd className="tabular-nums">{row.last_daily_note_at ? humanDate(row.last_daily_note_at, true) : <span className="text-slate-400">—</span>}</dd>
          </div>
        </dl>

        {/* Care plan status */}
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <CarePlanBadge status={row.care_plan_status} />
          {row.next_review_date && (
            <span className="text-[10px] text-slate-500 tabular-nums">Next review {humanDate(row.next_review_date)}</span>
          )}
        </div>

        {/* Primary needs */}
        {primaryNeeds.length > 0 && (
          <div className="mt-3">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Primary Needs</div>
            <div className="flex flex-wrap gap-1">
              {primaryNeeds.map((n) => (
                <span key={n} className="inline-flex px-1.5 py-0.5 text-[10px] rounded-full bg-sky-50 text-sky-700 border border-sky-200">{n}</span>
              ))}
              {extraNeeds > 0 && <span className="text-[10px] text-slate-500">+{extraNeeds}</span>}
            </div>
          </div>
        )}

        {/* Allergies / warnings */}
        {shortAllergies.length > 0 && (
          <div className="mt-3">
            <div className="text-[9px] uppercase tracking-widest text-red-700 font-semibold mb-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" aria-hidden="true" /> Allergies / Warnings
            </div>
            <div className="flex flex-wrap gap-1">
              {shortAllergies.map((a) => <AllergyPill key={a} item={a} />)}
              {extraAllergies > 0 && <span className="text-[10px] text-slate-500">+{extraAllergies}</span>}
            </div>
          </div>
        )}
      </div>

      <div className="p-3 border-t border-slate-100 bg-slate-50/50 rounded-b-xl">
        <Button
          size="sm"
          onClick={() => onOpen(row)}
          className="w-full h-11 lg:h-8 bg-sky-700 hover:bg-sky-800 text-white"
          data-testid={`card-view-profile-${row.id}`}
        >View Profile</Button>
      </div>
    </article>
  );
}

function ArchiveDialog({ open, onOpenChange, target, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      await onConfirm(reason.trim());
      onOpenChange(false);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white" data-testid="archive-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Archive service user</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {target?.full_name} will be hidden from directories, dashboards and rotas.
            Historical records (care notes, MAR, incidents) stay intact for audits and can be reopened at any time.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <label className="text-xs font-medium text-slate-700" htmlFor="archive-reason">Reason (optional)</label>
          <Textarea
            id="archive-reason"
            data-testid="archive-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Moved to residential care, discharged home, deceased…"
            className="mt-1 text-sm"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="archive-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-slate-800 hover:bg-slate-900" data-testid="archive-confirm">
            {busy ? "Archiving…" : "Archive service user"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ServiceUsers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const module = MODULES.clients;

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ active: 0, alerts_risks: 0, reviews_due: 0, missing_info: 0 });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [siteId, setSiteId] = useState("all");
  const [statusFilter, setStatusFilter] = useState("live");   // live | active | inactive | archived | all
  const [attention, setAttention] = useState("all");          // all | needs_attention
  const [sort, setSort] = useState("name_asc");
  const [view, setView] = useState("cards");                  // cards | list
  const [page, setPage] = useState(1);
  const [sites, setSites] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);

  const canManage = ["admin", "manager", "registered_manager", "deputy_manager", "service_manager", "super_admin"].includes(
    (user?.role || "").toLowerCase()
  );

  const load = async () => {
    setLoading(true);
    try {
      const [dirRes, sitesRes] = await Promise.all([
        api.get(`/clients/directory`, { params: { include_archived: true } }),
        api.get("/sites").catch(() => ({ data: [] })),
      ]);
      setRows(dirRes.data?.clients || []);
      setTotals(dirRes.data?.totals || { active: 0, alerts_risks: 0, reviews_due: 0, missing_info: 0 });
      setSites(sitesRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [query, siteId, statusFilter, attention, sort]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = rows.filter((r) => {
      // status filter
      if (statusFilter === "live" && r.archived) return false;
      if (statusFilter === "archived" && !r.archived) return false;
      if (statusFilter === "active" && (r.archived || r.status !== "active")) return false;
      if (statusFilter === "inactive" && (r.archived || r.status === "active")) return false;
      // site filter
      if (siteId !== "all") {
        if (siteId === "unassigned" && r.site_id) return false;
        if (siteId !== "unassigned" && r.site_id !== siteId) return false;
      }
      // attention filter
      if (attention === "needs_attention" && !r.needs_attention) return false;
      // search
      if (q) {
        const hay = [
          r.full_name, r.preferred_name, r.site_name, ...(r.primary_needs || []), ...(r.allergies || []),
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    const cmp = {
      name_asc:  (a, b) => (a.full_name || "").localeCompare(b.full_name || ""),
      name_desc: (a, b) => (b.full_name || "").localeCompare(a.full_name || ""),
      updated:   (a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""),
      review:    (a, b) => {
        const A = a.next_review_date || "9999-12-31";
        const B = b.next_review_date || "9999-12-31";
        return A.localeCompare(B);
      },
      attention: (a, b) => (Number(b.needs_attention) - Number(a.needs_attention)) || (a.full_name || "").localeCompare(b.full_name || ""),
    };
    out.sort(cmp[sort] || cmp.name_asc);
    return out;
  }, [rows, query, siteId, statusFilter, attention, sort]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openProfile = (r) => navigate(`/clients/${r.id}`);
  const openCarePlan = (r) => navigate(`/clients/${r.id}/care-planning`);
  const openMedication = (r) => navigate(`/clients/${r.id}/medication`);
  const openDocuments = (r) => navigate(`/clients/${r.id}/documentation`);

  const submitEdit = async (values) => {
    try {
      if (editing) {
        await api.put(`/clients/${editing.id}`, { data: values });
        toast.success("Service user updated");
      } else {
        await api.post(`/clients`, { data: values });
        toast.success("Service user created");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  };

  const doArchive = async (reason) => {
    try {
      await api.post(`/clients/${archiveTarget.id}/archive`, { reason });
      toast.success(`${archiveTarget.full_name} archived`);
      setArchiveTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  };

  const doRestore = async (row) => {
    if (!window.confirm(`Restore ${row.full_name} from archive?`)) return;
    try {
      await api.post(`/clients/${row.id}/restore`);
      toast.success(`${row.full_name} restored`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleExportPdf = async () => {
    try {
      await downloadBlob(`/reports/module/clients`, `ayobell-service-users.pdf`);
      toast.success("PDF downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-slate-50">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-4 lg:px-6 py-4 shrink-0">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">People</div>
            <h1 className="font-heading text-xl font-semibold text-slate-900" data-testid="page-title">Service Users</h1>
            <p className="text-sm text-slate-500 mt-0.5 hidden lg:block">View and manage people receiving care and support</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={load} className="h-10 lg:h-9" data-testid="btn-refresh">
              <RefreshCw className="w-3.5 h-3.5 lg:mr-1.5" /> <span className="hidden lg:inline">Refresh</span>
            </Button>
            {canManage && (
              <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)} className="h-10 lg:h-9 hidden lg:inline-flex" data-testid="btn-import-csv">
                <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Import CSV
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleExportPdf} className="h-10 lg:h-9 hidden lg:inline-flex" data-testid="btn-export">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Export
            </Button>
            {canManage && (
              <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} className="h-11 lg:h-9 bg-sky-700 hover:bg-sky-800" data-testid="btn-new-service-user">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> <span className="hidden sm:inline">Add Service User</span><span className="sm:hidden">Add</span>
              </Button>
            )}
          </div>
        </div>

        {/* Filters row — full search on mobile + filter drawer trigger */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px] lg:flex-none lg:w-72">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search name, needs, allergies…"
              value={query} onChange={(e) => setQuery(e.target.value)}
              className="w-full h-10 lg:h-9 pl-8 text-sm bg-white"
              data-testid="filter-search"
            />
          </div>

          {/* Mobile: single filter drawer trigger. Desktop: inline selects */}
          <MobileFilterDrawer
            activeCount={
              (siteId !== "all" ? 1 : 0) +
              (statusFilter !== "live" ? 1 : 0) +
              (attention !== "all" ? 1 : 0)
            }
            onClear={() => { setSiteId("all"); setStatusFilter("live"); setAttention("all"); }}
          >
            <div className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Site / House</label>
                <Select value={siteId} onValueChange={setSiteId}>
                  <SelectTrigger className="w-full h-11 text-sm bg-white" data-testid="mobile-filter-site"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="all">All sites</SelectItem>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Status</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full h-11 text-sm bg-white" data-testid="mobile-filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="live">Live (non-archived)</SelectItem>
                    <SelectItem value="active">Active only</SelectItem>
                    <SelectItem value="inactive">Inactive / respite / hospitalised</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                    <SelectItem value="all">All (incl. archived)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Attention</label>
                <Select value={attention} onValueChange={setAttention}>
                  <SelectTrigger className="w-full h-11 text-sm bg-white" data-testid="mobile-filter-attention"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="needs_attention">Needs attention</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Sort</label>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger className="w-full h-11 text-sm bg-white" data-testid="mobile-filter-sort"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="name_asc">Name A–Z</SelectItem>
                    <SelectItem value="name_desc">Name Z–A</SelectItem>
                    <SelectItem value="updated">Recently updated</SelectItem>
                    <SelectItem value="review">Review due soon</SelectItem>
                    <SelectItem value="attention">Needs attention</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </MobileFilterDrawer>

          {/* Desktop inline filters */}
          <div className="hidden lg:flex items-center gap-2 flex-wrap">
          <Select value={siteId} onValueChange={setSiteId}>
            <SelectTrigger className="w-48 h-9 text-sm bg-white" data-testid="filter-site">
              <SelectValue placeholder="Site / House" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All sites</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 h-9 text-sm bg-white" data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="live">Live (non-archived)</SelectItem>
              <SelectItem value="active">Active only</SelectItem>
              <SelectItem value="inactive">Inactive / respite / hospitalised</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
              <SelectItem value="all">All (incl. archived)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={attention} onValueChange={setAttention}>
            <SelectTrigger className="w-44 h-9 text-sm bg-white" data-testid="filter-attention">
              <SelectValue placeholder="Attention" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="needs_attention">Needs attention</SelectItem>
            </SelectContent>
          </Select>
          </div>

          <div className="ml-auto hidden lg:flex items-center gap-2">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-44 h-9 text-sm bg-white" data-testid="filter-sort">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="name_asc">Name A–Z</SelectItem>
                <SelectItem value="name_desc">Name Z–A</SelectItem>
                <SelectItem value="updated">Recently updated</SelectItem>
                <SelectItem value="review">Review due soon</SelectItem>
                <SelectItem value="attention">Needs attention</SelectItem>
              </SelectContent>
            </Select>

            <div className="inline-flex rounded-md border border-slate-200 bg-white overflow-hidden" role="group" aria-label="View toggle">
              <button
                onClick={() => setView("cards")}
                className={`h-9 px-2.5 text-xs font-medium inline-flex items-center gap-1 ${view === "cards" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                aria-pressed={view === "cards"}
                data-testid="view-cards"
              ><LayoutGrid className="w-3.5 h-3.5" /> Cards</button>
              <button
                onClick={() => setView("list")}
                className={`h-9 px-2.5 text-xs font-medium inline-flex items-center gap-1 border-l border-slate-200 ${view === "list" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                aria-pressed={view === "list"}
                data-testid="view-list"
              ><ListIcon className="w-3.5 h-3.5" /> List</button>
            </div>
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="px-4 lg:px-6 pt-4 pb-2 grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-3">
        <KpiCard label="Active Service Users" value={totals.active} icon={Users2} tone="sky" testid="kpi-active" />
        <KpiCard label="Alerts / Risks" value={totals.alerts_risks} icon={ShieldAlert} tone="rose" testid="kpi-alerts" />
        <KpiCard label="Reviews Due" value={totals.reviews_due} icon={CalendarClock} tone="amber" testid="kpi-reviews" />
        <KpiCard label="Missing Information" value={totals.missing_info} icon={Info} tone="default" testid="kpi-missing" />
      </div>

      {/* Grid / List */}
      <div className="flex-1 overflow-auto px-4 lg:px-6 pb-6" data-testid="clients-scroll">
        {loading ? (
          <div className="p-16 text-center text-sm text-slate-500" data-testid="clients-loading">Loading service users…</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center bg-white border border-slate-200 rounded-lg" data-testid="clients-empty">
            <Users2 className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <div className="text-sm text-slate-600">No service users match your filters.</div>
          </div>
        ) : view === "cards" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="clients-grid">
            {pageRows.map((r) => (
              <ServiceUserCard
                key={r.id}
                row={r}
                onOpen={openProfile}
                onEdit={(x) => { setEditing(x); setDialogOpen(true); }}
                onCarePlan={openCarePlan}
                onMedication={openMedication}
                onDocuments={openDocuments}
                onArchive={(x) => setArchiveTarget(x)}
                onRestore={doRestore}
                canArchive={canManage}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="clients-list">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                <tr>
                  <th className="text-left px-3 py-2">Service User</th>
                  <th className="text-left px-3 py-2">Site</th>
                  <th className="text-left px-3 py-2">Care Plan</th>
                  <th className="text-left px-3 py-2">Meds</th>
                  <th className="text-left px-3 py-2">Last Note</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="w-16 px-2 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => openProfile(r)}
                    data-testid={`list-row-${r.id}`}
                  >
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <PersonAvatar person={r} name={r.preferred_name || r.full_name} size="sm" />
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 truncate">{r.preferred_name || r.full_name}</div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {r.dob ? `${humanDate(r.dob)}${r.age != null ? ` · ${r.age}y` : ""}` : "—"}
                          </div>
                        </div>
                        {r.needs_attention && !r.archived && <AttentionChip reasons={r.attention_reasons} />}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{r.site_name || <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-2"><CarePlanBadge status={r.care_plan_status} /></td>
                    <td className="px-3 py-2 tabular-nums">{r.active_med_count}</td>
                    <td className="px-3 py-2 text-slate-600 tabular-nums">{r.last_daily_note_at ? humanDate(r.last_daily_note_at) : <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-2"><StatusBadge status={r.status} archived={r.archived} /></td>
                    <td className="px-2 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => { setEditing(r); setDialogOpen(true); }}
                        className="p-1 text-slate-500 hover:text-sky-700"
                        aria-label="Edit"
                        data-testid={`list-edit-${r.id}`}
                      ><Pencil className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {filtered.length > PAGE_SIZE && (
          <div className="mt-4 flex items-center justify-between text-xs text-slate-600" data-testid="pagination">
            <span>Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}</span>
            <div className="inline-flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="h-8 px-2 rounded-md border border-slate-200 bg-white disabled:opacity-50 inline-flex items-center gap-1"
                data-testid="page-prev"
              ><ChevronLeft className="w-3.5 h-3.5" /> Prev</button>
              <span className="tabular-nums">Page {page} / {pageCount}</span>
              <button
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                disabled={page >= pageCount}
                className="h-8 px-2 rounded-md border border-slate-200 bg-white disabled:opacity-50 inline-flex items-center gap-1"
                data-testid="page-next"
              >Next <ChevronRight className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        )}
      </div>

      <RecordDialog
        open={dialogOpen}
        onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditing(null); }}
        module={module}
        record={editing}
        onSubmit={submitEdit}
      />
      <BulkImportDialog open={bulkOpen} onOpenChange={setBulkOpen} module={module} onDone={load} />
      <ArchiveDialog
        open={!!archiveTarget}
        onOpenChange={(v) => { if (!v) setArchiveTarget(null); }}
        target={archiveTarget}
        onConfirm={doArchive}
      />
    </div>
  );
}
